import {
  Component,
  OnDestroy,
  OnInit,
  Inject,
  ViewChild,
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';

import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  filter,
  forkJoin,
  merge,
  Observable,
  of,
  Subscription,
  switchMap, take,
  tap,
} from 'rxjs';

import { WebSocketEventsEnum } from '@shared/websocket';
import { Location } from '@shared/location/models';
import { Resource } from '@shared/resource/models';
import { Service } from '@shared/service/models';
import { PartySource, PartyTypes } from '@shared/party/enums';
import { Notification } from '@shared/party/models';
import { Account } from '@shared/account/models';
import {
  OrderByValues,
  PageTitleService, SearchBarComponent,
} from '@tablesready/common-ui';
import {
  MarkPartyService,
  ResourcesService,
  ServicesService,
} from '../services';
import { HistoryService } from './services';
import { IHistoryInputData, IParty } from './models';
import { ChatPartyComponent } from '../components';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';
import { HeaderTitleService } from '@tablesready/frontend-header';
import { AuthRestaurantService, SocketService } from '@tablesready/frontend-store';
import { AlertsService, ClockService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { CheckoutStatus } from '@shared/stripe/enums';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';
import { InfiniteScrollCustomEvent, IonContent, ScrollDetail } from '@ionic/angular';

@Component({
  selector: 'tablesready-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.scss'],
  animations: [
    trigger('moveParty', [
      state('expanded', style({ opacity: 1 })),
      state('collapsed', style({ opacity: 0 })),
      transition('expanded => collapsed', [animate('200ms ease-out')]),
    ]),
  ],
})
export class HistoryComponent implements OnInit, OnDestroy {
  @ViewChild(IonContent) ionContent?: IonContent;
  @ViewChild('searchBarRef') searchBarRef?: SearchBarComponent;
  icon = AppIcon;
  resourceSettings: any;
  serviceSettings: any;
  compact: any;
  customer: any;
  reservation: any;
  timezone: string;
  customer_plural: any;
  now: number;
  parties: IParty[] = [];
  recent_search_query: string = '';

  sortBy: string = 'close_time';
  sortReverse: boolean = true;
  resources: Resource[] = [];
  services: Service[] = [];
  page:number = 1;
  historyCount: number = 0;
  loading: boolean;
  location: Location;
  partyTypes = PartyTypes;
  account: Account;

  private limit: number = 40;
  private debounceMs = 200;
  private infiniteScrollEvent?: InfiniteScrollCustomEvent;
  private historyInputData = new BehaviorSubject<IHistoryInputData>(undefined);
  private subscription = new Subscription();

  constructor(
    private readonly alertsService: AlertsService,
    private readonly clockService: ClockService,
    private readonly resourcesService: ResourcesService,
    private readonly historyService: HistoryService,
    private readonly socketService: SocketService,
    private readonly markPartyService: MarkPartyService,
    private readonly pageTitleService: PageTitleService,
    private readonly modalController: FrontendModalService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly headerService: HeaderTitleService,
    private readonly serviceService: ServicesService,
    @Inject(APP_VARIABLES_TOKEN) private readonly appVariables: AppVariables,
  ) { }

  ionViewWillEnter(): void {
    void this.ionContent?.scrollToTop(0);
  }

  onIonInfinite(ev: InfiniteScrollCustomEvent): void {
    this.infiniteScrollEvent = ev;
    this.loadMore();
  }

  ionViewWillLeave(): void {
    this.resetFilters();
    if (!this.searchBarRef) {
      return;
    }

    this.searchBarRef.clearSearchField();
  }

  updateSearchQuery(query: string): void {
    this.recent_search_query = query;
    this.setLoadingState();
    this.emitNextEvent();
  }

  ngOnInit(): void {
    this.pageTitleService.setTitle('History');

    this.subscription.add(this.clockService.clock$.subscribe(now => this.now = now));

    this.subscription.add(
      this.historyInputData.asObservable()
        .pipe(
          filter(data => Boolean(data)),
          switchMap((options: IHistoryInputData) => forkJoin(this.updateParties(options), of(options.page))),
          tap(([response, page]) => {
            const { parties, count } = response;
            this.updatePartiesArray(parties, page);
            this.headerService.setDataForRoute('history', { badgeCount: Number(count) });

            if (this.infiniteScrollEvent) {
              void this.infiniteScrollEvent.target.complete();
              this.infiniteScrollEvent = null;
            }

            if (!parties.length) {
              return false;
            }
            this.page = ++page;
            this.loading = false;
            this.historyCount = count;
          }),
        ).subscribe());

    const partiesStream = this.socketService.getEventStream(WebSocketEventsEnum.parties);
    const reservationStream = this.socketService.getEventStream(WebSocketEventsEnum.reservations);

    this.subscription.add(
      merge(reservationStream, partiesStream)
        .pipe(
          debounceTime(this.debounceMs),
          tap(()=> {
            this.page = 1;
          })
        ).subscribe(
          () => {
            this.emitNextEvent();
          }
        )
    )

    this.subscription.add(
      combineLatest([
        this.authRestaurantService.selectWithAccount()
          .pipe(
            tap(({location, account}) => {
              this.location = location;
              this.account = account;
              this.resourceSettings = location.resourceSettings;
              this.serviceSettings = location.serviceSettings;
              this.compact = location.displayOptions.compact || false;
              this.customer = location.displayOptions.labels.customer ;
              this.reservation = location.displayOptions.labels.reservation;
              this.customer_plural = location.displayOptions.labels.customer_plural;
              this.timezone = location.timezone;

              const title = `Recent ${this.customer_plural} History`;
              this.headerService.setDataForRoute('history', { title });
            })),
        this.resourcesService.selectAll().pipe(tap(resources => this.resources = resources)),
        this.serviceService.selectAll().pipe(tap(services => this.services = services)),
      ]).subscribe(),
    );
  }

  private emitNextEvent(page = 1): void {
    const initOptions: IHistoryInputData = {
      page: page,
      sortBy: this.sortBy,
      sortReverse:this.sortReverse,
      limit: this.limit,
      search: this.recent_search_query
    };
    this.historyInputData.next(initOptions);
  }

  public orderBy({ orderBy, orderAscending }: OrderByValues): void {
    this.sortReverse = orderAscending;
    this.sortBy = orderBy;
    this.setLoadingState();
    this.emitNextEvent();
  }

  private setLoadingState(): void {
    this.page = 1;
    this.parties = [];
    this.loading = true;
  }

  public reason(party: IParty): string {
    if (party.completedAt > 0) {
      return 'Completed';
    }
    if (party.seated_time && !party.canceled_time) {
      return 'Served';
    }
    if (party.canceled_time) {
      return 'Canceled';
    }
  }

  reasonTime(party: IParty): number {
    if (party.completedAt > 0) {
      return this.now - party.completedAt;
    }

    if (party.seated_time && !party.canceled_time) {
      return this.now - party.seated_time;
    }

    if (party.canceled_time) {
      return this.now - party.canceled_time;
    }

    return this.now - party.close_time;
  }

  private getSortProp(sortBy: string, sortReverse: boolean) {
    const sort: any = {};
    sort[this.sortBy] = sortReverse ? -1 : 1;

    return sort;
  }

  private updateParties (options: IHistoryInputData): Observable<any> {
    this.loading = true;
    let {page, limit, sortBy, sortReverse, search} = options;
    const sort = this.getSortProp(sortBy, sortReverse);

    return this.historyService.history({
      page,
      limit,
      search,
      sort
    });
  }

  private updatePartiesArray(parties: Array<IParty>, page = 1): void {
    if (page === 1) {
      this.parties = parties;
    } else {
      this.parties = Array.from(new Set([...this.parties, ...parties]));
    }
  }

  partyTypeNgClass(party: IParty):string {
    if (party.type === PartyTypes.reservation) {
      return 'badge badge-pill badge-danger';
    }

    if (party.source === PartySource.public) {
      return 'badge badge-pill badge-info';
    }

    if (party.source === PartySource.sms) {
      return 'badge badge-pill badge-warning';
    }

    if (party.source === PartySource.square) {
      return 'badge badge-pill badge-dark';
    }

    return 'd-none';
  }

  getBtnName(party: IParty): string {
    if (party.completedAt > 0) {
      return 'Undo Completed';
    }

    if (party.checkin_time && party.seated_time && !party.canceled_time) {
      return 'Undo Served';
    }

    if (party.canceled_time) {
      return 'Undo Canceled';
    }

    return '';
  }

  moveToWaitlist(party: IParty): void {
    let duration = 0;

    if (party.services && party.services.length > 0) {
      duration = this.serviceService.getServicesByIds(party.services).reduce((sum, service) => {
        sum += service.duration.miliseconds;

        return sum;
      }, 0);
    }

    const options = {
      party_id: party._id,
      seated_time: party.completedAt > 0 || (party.seated_time && party.canceled_time) ? party.seated_time : null,
      canceled_time: null,
      completedAt: 0,
      duration,
    };

    this.subscription.add(
      this.markPartyService.setParty(options)
        .subscribe(
          () => {
            party.isHidden = true;
            this.alertsService.add({
              type: IAlertTypes.success,
              local: false,
              heading: `Moved to ${party.type === this.partyTypes.togo ? 'To Go' : 'waitlist'}:`,
              body: `${party.name} ${this.customer} of ${party.size}`,
            });
          }
        )
    )
  }

  loadMore() {
    if (this.loading) {
      return false;
    }
    this.loading = true;
    this.emitNextEvent(this.page);
  }

  isArray(val: any): boolean {
    return val instanceof Array;
  };

  isInProgressStatus(message: Notification) {
    return message.status_sent === 'inProcess' && message.time >= (this.now - 60000);
  }

  isSentSmsStatus(message: Notification) {
    return message.status_sent === 'sent' && message.type === 'sms';
  }

  isSentVoiceStatus(message: Notification) {
    return message.status_sent === 'sent' && message.type === 'voice';
  }

  isNotSentStatus(message: Notification) {
    return message.status_sent === 'not_sent' || (message.status_sent === 'inProcess' && message.time < (this.now - 60000));
  }

  getPartyNotifications(party: IParty) {
    const messages = [];
    if (party.notifications) {
      messages.push(...party.notifications.slice(-4));
    }
    if (party.inbounds) {
      messages.push(...party.inbounds.slice(-4));
    }
    messages.sort((a, b) => a.time - b.time);

    return messages.slice(-4);
  }

  showViewAllMessages(party: IParty) {
    return [...party.notifications, ...(party.inbounds || [])].length > 4;
  }

  unpaid(party: IParty): boolean {
    return party.stripeData?.status === CheckoutStatus.inProgress
      && (party.created_time + this.appVariables.UNPAID_PARTY_LIFETIME * 1000) > Date.now();
  }

  paid(party: IParty): boolean {
    return party.stripeData?.status === CheckoutStatus.paid;
  }

  refunded(party: IParty): boolean {
    return party.stripeData?.status === CheckoutStatus.refunded;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  viewMessagesHistory(party: IParty) {
    this.modalController.create<ChatPartyComponent, unknown>(ChatPartyComponent, {
      location: this.location,
      party,
      showReplyForm: false,
    })
      .pipe(take(1))
      .subscribe();
  }

  private resetFilters(): void {
    this.page = 1;
    this.sortBy = 'close_time';
    this.sortReverse = true;
    this.recent_search_query = '';

    this.historyInputData.next({
      page: this.page,
      limit: this.limit,
      sortBy: this.sortBy,
      sortReverse: this.sortReverse,
      search: this.recent_search_query,
    });
  }
}
